<script setup lang="ts">
import type { PublicPlatformSettings, WebsiteSettings } from '@/api/website'
import { PhWechatLogo, PhX, PhArrowUpRight } from '@phosphor-icons/vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  webUrl: string
  websiteSettings: WebsiteSettings
  platform: PublicPlatformSettings | null
}>()
const emit = defineEmits<{ 'retry-contact': [] }>()
function retryContact() {
  imageFailed.value = false
  emit('retry-contact')
}
function toggleContact() {
  if (pinned.value) closeContact()
  else pinned.value = true
}
const contactRoot = ref<HTMLElement | null>(null)
const cooperationRoot = ref<HTMLElement | null>(null)
const cooperationOpen = ref(false)
const pinned = ref(false)
const hovered = ref(false)
const imageFailed = ref(false)
const contactOpen = computed(() => pinned.value || hovered.value)
watch(
  () => props.platform?.customerServiceQrUrl,
  () => {
    imageFailed.value = false
  },
)
function closeContact() {
  pinned.value = false
  hovered.value = false
}
function outside(event: PointerEvent) {
  if (!contactRoot.value?.contains(event.target as Node)) closeContact()
  if (!cooperationRoot.value?.contains(event.target as Node)) cooperationOpen.value = false
}
function escape(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeContact()
    cooperationOpen.value = false
  }
}
onMounted(() => {
  document.addEventListener('pointerdown', outside)
  document.addEventListener('keydown', escape)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', outside)
  document.removeEventListener('keydown', escape)
})
</script>

<template>
  <footer class="site-footer">
    <div id="cooperation" class="closing-cta">
      <div class="cooperation-art" aria-hidden="true"><i></i><i></i><i></i><span></span></div>
      <div class="container">
        <div class="cooperation-copy">
          <h2>让科学教育，拥有更多可能</h2>
          <p>面向高校、学校与教育机构，定制教学应用服务平台与内容共建</p>
        </div>
        <div
          ref="cooperationRoot"
          class="cooperation-entry"
          @focusout="
            (event) => {
              if (!cooperationRoot?.contains(event.relatedTarget as Node)) cooperationOpen = false
            }
          "
        >
          <button
            class="cooperation-trigger"
            type="button"
            :aria-expanded="cooperationOpen"
            aria-controls="cooperation-qr"
            @click="cooperationOpen = !cooperationOpen"
          >
            洽谈合作 <PhArrowUpRight :size="20" />
          </button>
          <div
            v-if="cooperationOpen"
            id="cooperation-qr"
            class="contact-popover cooperation-popover"
            role="region"
            aria-label="合作咨询二维码"
          >
            <button
              class="contact-close"
              type="button"
              aria-label="关闭合作二维码"
              @click="cooperationOpen = false"
            >
              <PhX :size="18" />
            </button>
            <template v-if="platform?.customerServiceQrUrl && !imageFailed">
              <img
                :src="platform.customerServiceQrUrl"
                alt="合作咨询二维码"
                @error="imageFailed = true"
              />
              <p>扫码咨询合作</p>
            </template>
            <template v-else>
              <p>联系二维码暂时无法显示</p>
              <button class="contact-retry" type="button" @click="retryContact">重新加载</button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div id="about" class="container footer-main">
      <div class="footer-brand">
        <img v-if="websiteSettings.logoUrl" :src="websiteSettings.logoUrl" alt="麦汁实验室" />
        <span v-else class="image-placeholder logo-placeholder">品牌 Logo</span>
        <p>让科学看得见，让探索更简单。</p>
        <div
          ref="contactRoot"
          class="contact-entry"
          @pointerenter="
            (event) => {
              if (event.pointerType === 'mouse') hovered = true
            }
          "
          @pointerleave="hovered = false"
          @focusout="
            (event) => {
              if (!contactRoot?.contains(event.relatedTarget as Node)) closeContact()
            }
          "
        >
          <button
            class="contact-trigger"
            type="button"
            :aria-expanded="contactOpen"
            aria-controls="contact-qr"
            aria-label="联系我们"
            @click="toggleContact"
          >
            <PhWechatLogo :size="22" weight="duotone" />
            <span>联系我们</span>
          </button>
          <div
            v-if="contactOpen"
            id="contact-qr"
            class="contact-popover"
            role="region"
            aria-label="联系我们二维码"
          >
            <button
              class="contact-close"
              type="button"
              aria-label="关闭联系二维码"
              @click="closeContact"
            >
              <PhX :size="18" />
            </button>
            <template v-if="platform?.customerServiceQrUrl && !imageFailed">
              <img
                :src="platform.customerServiceQrUrl"
                alt="麦汁实验室客服二维码"
                @error="imageFailed = true"
              />
              <p>{{ platform.customerServiceDescription || '扫码联系我们' }}</p>
            </template>
            <template v-else
              ><p>联系二维码暂时无法显示</p>
              <button class="contact-retry" type="button" @click="retryContact">
                重新加载
              </button></template
            >
          </div>
        </div>
      </div>
      <div class="footer-links">
        <div>
          <strong>产品</strong>
          <a href="#product">产品介绍</a>
          <a href="#subjects">实验内容</a>
          <a href="#download">使用版本</a>
        </div>
        <div>
          <strong>支持</strong>
          <a href="#download">下载中心</a>
          <a :href="webUrl">开始实验</a>
          <a href="#cooperation">商务合作</a>
        </div>
        <div>
          <strong>服务条款</strong>
          <a href="/legal/user-agreement" target="_blank" rel="noopener noreferrer">用户协议</a>
          <a href="/legal/privacy-policy" target="_blank" rel="noopener noreferrer">隐私政策</a>
          <a href="/legal/children-privacy-policy" target="_blank" rel="noopener noreferrer">
            儿童个人信息保护规则
          </a>
        </div>
        <div>
          <strong>信息透明</strong>
          <a
            href="/legal/personal-information-collection-list"
            target="_blank"
            rel="noopener noreferrer"
          >
            个人信息收集清单
          </a>
          <a
            href="/legal/third-party-information-sharing-list"
            target="_blank"
            rel="noopener noreferrer"
          >
            第三方信息共享清单
          </a>
          <a href="/legal/system-permission-usage" target="_blank" rel="noopener noreferrer">
            系统权限使用说明
          </a>
        </div>
      </div>
    </div>
    <div class="container footer-legal">
      <span
        >© {{ new Date().getFullYear() }} {{ websiteSettings.copyrightOwner || '麦汁实验室' }}</span
      >
      <a
        v-if="websiteSettings.icpRecordNumber"
        class="icp-link"
        :href="websiteSettings.icpRecordUrl || undefined"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ websiteSettings.icpRecordNumber }}
      </a>
      <a
        v-if="websiteSettings.publicSecurityRecordNumber"
        class="public-security-link"
        :href="websiteSettings.publicSecurityRecordUrl || undefined"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img
          v-if="websiteSettings.publicSecurityIconUrl"
          class="public-security-icon"
          :src="websiteSettings.publicSecurityIconUrl"
          alt="公安备案"
        />
        {{ websiteSettings.publicSecurityRecordNumber }}
      </a>
    </div>
  </footer>
</template>
